import { cn } from '@/lib/utils';
import { useMemo } from 'react';

import { useChatSession, useConfig } from '@chainlit/react-client';

import Starter from './Starter';

/** How many suggestions the empty chat shows. The catalogue may hold more; an
 *  empty chat is not a menu to read before you are allowed to type. */
const MAX_VISIBLE = 5;

interface Props {
  className?: string;
}

export default function Starters({ className }: Props) {
  const { chatProfile } = useChatSession();
  const { config } = useConfig();

  const starters = useMemo(() => {
    if (chatProfile) {
      const selectedChatProfile = config?.chatProfiles.find(
        (profile) => profile.name === chatProfile
      );
      if (selectedChatProfile?.starters) {
        return selectedChatProfile.starters;
      }
    }
    return config?.starters;
  }, [config, chatProfile]);

  const starterCategories = config?.starterCategories;

  // Flattened deliberately. This used to be subject chips that expanded into
  // questions: the first click did nothing visible except open a second row, so
  // people clicked a subject expecting an answer and got another menu. One row
  // of real questions says what it does.
  const flattened = useMemo(() => {
    if (!starterCategories?.length) return [];
    const out: { label: string; message: string; icon?: string }[] = [];
    const seen = new Set<string>();
    // Round-robin across subjects so five suggestions are not all one topic.
    const lists = starterCategories.map((c) => c.starters ?? []);
    for (let i = 0; out.length < MAX_VISIBLE; i++) {
      let added = false;
      for (const list of lists) {
        if (i >= list.length) continue;
        added = true;
        const st = list[i];
        const key = st.message?.trim().toLowerCase();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        out.push(st);
        if (out.length >= MAX_VISIBLE) break;
      }
      if (!added) break;
    }
    return out;
  }, [starterCategories]);

  if (flattened.length) {
    return (
      <div
        id="starters"
        className={cn('flex gap-2 justify-center flex-wrap', className)}
      >
        {flattened.map((starter) => (
          <Starter key={starter.message} starter={starter} />
        ))}
      </div>
    );
  }

  if (!starters?.length) return null;

  return (
    <div
      id="starters"
      className={cn('flex gap-2 justify-center flex-wrap', className)}
    >
      {starters.map((starter, i) => (
        <Starter key={i} starter={starter} />
      ))}
    </div>
  );
}
